import { useThreadAccess } from '@components/ArtBoard/Threads/hooks'
import type { ID, WithId } from '@octopus-do/octopus-project-core'
import { ThemeColors, TinyDropdownMenu } from '@octopus-do/octopus-storybook'
import { selectLatestForeignCreatedAt } from '@store/reducers/commentReads'
import type React from 'react'
import { useSelector } from 'react-redux'

import { createSwitch } from '@/utils'
import { CommentSettingsTrigger } from '../CommentSettingsTrigger'
import { Content } from './components'
import { useThreadActions } from './hooks'

interface Props extends WithId {
  onEdit: () => void
  author: ID | null
  resolved: boolean
}

export const ThreadActions: React.FC<Props> = ({
  id,
  onEdit,
  author,
  resolved,
}) => {
  const { onCopyLink, onDelete, onMarkUnread } = useThreadActions(id)
  const foreign = useSelector(selectLatestForeignCreatedAt(id))

  const canDelete = useThreadAccess(author, 'delete')
  const canEdit = useThreadAccess(author, 'edit')
  // only foreign comments count as unread, so there must be one to mark; a
  // guest never has any, a resolved thread never lights up
  const canMarkUnread = foreign !== null && !resolved

  const switcher = createSwitch()
    .case(['edit'], onEdit)
    .case(['copy-link'], onCopyLink)
    .case(['mark-unread'], onMarkUnread)
    .case(['delete'], onDelete)

  const onClick = (keys: string[]) => switcher.run(keys)

  return (
    <TinyDropdownMenu.Root
      onClick={onClick}
      content={Content}
      sideOffset={8}
      theme={ThemeColors.light}
      canEdit={canEdit}
      canDelete={canDelete}
      canMarkUnread={canMarkUnread}
    >
      <CommentSettingsTrigger />
    </TinyDropdownMenu.Root>
  )
}
